/**
 * Locale Pinia store
 * Manages the current language preference and persists to localStorage
 */

import { defineStore } from 'pinia';
import { ref } from 'vue';
import { loadLocale } from '../locales';

export type LocaleCode = 'en' | 'es' | 'fr' | 'de' | 'it' | 'sl';

export const LOCALE_STORAGE_KEY = 'otto-locale';
const DEFAULT_LOCALE: LocaleCode = 'en';

/**
 * Every language this build ships. The store's own list, and the one the
 * account preferences' picker narrows its server-side preference against
 * (`views/Account.vue`); the shell bar reads it too, to tell a language it can
 * follow from a value it cannot (`shell/localeSync.ts`, card t_b028a0f2).
 */
export const LOCALE_CODES: LocaleCode[] = ['en', 'es', 'fr', 'de', 'it', 'sl'];

/** Is `value` one of the codes this build has a catalogue for? */
export const isLocaleCode = (value: string): value is LocaleCode => (LOCALE_CODES as readonly string[]).includes(value);

export const useLocaleStore = defineStore('locale', () => {
  // ── State ────────────────────────────────────────────────────────────────
  const currentLocale = ref<LocaleCode>(DEFAULT_LOCALE);

  // ── Initialization ────────────────────────────────────────────────────────
  const initializeLocale = () => {
    if (typeof localStorage !== 'undefined') {
      const savedLocale = localStorage.getItem(LOCALE_STORAGE_KEY) as LocaleCode | null;
      if (savedLocale && isValidLocale(savedLocale)) {
        currentLocale.value = savedLocale;
      }
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────
  /** The code list's own spelling (`LOCALE_CODES`) — kept for this store's call sites. */
  const isValidLocale = (locale: string): locale is LocaleCode => isLocaleCode(locale);

  // ── Actions ──────────────────────────────────────────────────────────────
  /**
   * Set the active locale, load it in vue-i18n, and persist to localStorage
   */
  const setLocale = async (locale: LocaleCode) => {
    if (!isValidLocale(locale)) {
      console.warn(`Invalid locale: ${locale}. Using ${DEFAULT_LOCALE} instead.`);
      locale = DEFAULT_LOCALE;
    }

    try {
      // Load the locale in vue-i18n
      await loadLocale(locale);
      currentLocale.value = locale;

      // Persist to localStorage
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(LOCALE_STORAGE_KEY, locale);
      }
    } catch (err) {
      console.error(`Failed to set locale to ${locale}:`, err);
      // Fallback to English
      currentLocale.value = DEFAULT_LOCALE;
    }
  };

  return {
    currentLocale,
    initializeLocale,
    setLocale,
  };
});
